/**
 * Color utility functions for generating and manipulating colors
 */

/**
 * Generates a consistent color hash from a string name
 * @param name - The string to generate color from
 * @returns Hex color string (without #)
 */
export function generateColorFromName(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }

  const hex = Math.abs(hash).toString(16).padStart(6, '0').substring(0, 6)

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  if (brightness > 200) {
    return darkenColor(hex)
  }
  if (brightness < 100) {
    return lightenColor(hex)
  }

  return hex
}

/**
 * Lightens a hex color by increasing RGB values
 * @param hex - Hex color string (without #)
 * @returns Lightened hex color string
 */
export function lightenColor(hex: string): string {
  const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + 80)
  const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + 80)
  const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + 80)

  return r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0')
}

/**
 * Darkens a hex color by reducing RGB values
 * @param hex - Hex color string (without #)
 * @returns Darkened hex color string
 */
export function darkenColor(hex: string): string {
  const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - 80)
  const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - 80)
  const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - 80)

  return r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0')
}

/**
 * Converts hex color to RGBA string
 * @param hex - Hex color string (without #)
 * @param alpha - Alpha value (0-1)
 * @returns RGBA color string
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Checks if two colors are similar within tolerance
 * @param color1 - First hex color string (without #)
 * @param color2 - Second hex color string (without #)
 * @param tolerance - Color difference tolerance (default: 30)
 * @returns True if colors are similar
 */
export function colorsAreSimilar(color1: string, color2: string, tolerance: number = 30): boolean {
  const r1 = parseInt(color1.substring(0, 2), 16)
  const g1 = parseInt(color1.substring(2, 4), 16)
  const b1 = parseInt(color1.substring(4, 6), 16)

  const r2 = parseInt(color2.substring(0, 2), 16)
  const g2 = parseInt(color2.substring(2, 4), 16)
  const b2 = parseInt(color2.substring(4, 6), 16)

  const distance = Math.sqrt(Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2))

  return distance <= tolerance
}

/**
 * Calculate relative luminance of a color (WCAG 2.1)
 * @param r - Red component (0-255)
 * @param g - Green component (0-255)
 * @param b - Blue component (0-255)
 * @returns Relative luminance (0-1)
 */
export function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Calculate contrast ratio between two colors (WCAG 2.1)
 * @param color1 - First hex color string (without #)
 * @param color2 - Second hex color string (without #)
 * @returns Contrast ratio (1-21)
 */
export function getContrastRatio(color1: string, color2: string): number {
  const r1 = parseInt(color1.substring(0, 2), 16)
  const g1 = parseInt(color1.substring(2, 4), 16)
  const b1 = parseInt(color1.substring(4, 6), 16)

  const r2 = parseInt(color2.substring(0, 2), 16)
  const g2 = parseInt(color2.substring(2, 4), 16)
  const b2 = parseInt(color2.substring(4, 6), 16)

  const l1 = getRelativeLuminance(r1, g1, b1)
  const l2 = getRelativeLuminance(r2, g2, b2)

  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)

  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Check if contrast ratio meets WCAG AA standards
 * @param foreground - Foreground color hex string (without #)
 * @param background - Background color hex string (without #)
 * @param level - WCAG level ('AA' or 'AAA')
 * @returns True if contrast meets standards
 */
export function meetsWCAGContrast(foreground: string, background: string, level: 'AA' | 'AAA' = 'AA'): boolean {
  const ratio = getContrastRatio(foreground, background)
  return level === 'AA' ? ratio >= 4.5 : ratio >= 7
}

/**
 * Adjust color to meet minimum contrast ratio with background
 * @param foreground - Foreground color hex string (without #)
 * @param background - Background color hex string (without #)
 * @param minRatio - Minimum contrast ratio (default: 4.5 for WCAG AA)
 * @returns Adjusted foreground color hex string (without #)
 */
export function adjustColorForContrast(foreground: string, background: string, minRatio: number = 4.5): string {
  const [r, g, b] = [
    parseInt(foreground.substring(0, 2), 16),
    parseInt(foreground.substring(2, 4), 16),
    parseInt(foreground.substring(4, 6), 16),
  ]

  const bgLuminance = getRelativeLuminance(
    parseInt(background.substring(0, 2), 16),
    parseInt(background.substring(2, 4), 16),
    parseInt(background.substring(4, 6), 16),
  )

  // If background is dark, we need light text
  const targetLuminance = bgLuminance < 0.5 ? 0.9 : 0.1

  // Adjust color towards target luminance
  const currentLuminance = getRelativeLuminance(r, g, b)
  const factor = targetLuminance / currentLuminance

  const newR = Math.max(0, Math.min(255, Math.round(r * factor)))
  const newG = Math.max(0, Math.min(255, Math.round(g * factor)))
  const newB = Math.max(0, Math.min(255, Math.round(b * factor)))

  return newR.toString(16).padStart(2, '0') + newG.toString(16).padStart(2, '0') + newB.toString(16).padStart(2, '0')
}

/**
 * Generate WCAG-compliant tag colors
 * @param name - The string to generate color from
 * @returns Object with foreground and background colors
 */
export function generateWCAGCompliantTagColors(name: string): {
  foreground: string
  background: string
  foregroundHover: string
  backgroundHover: string
} {
  const baseColor = generateColorFromName(name)

  // Default background colors (light backgrounds)
  const lightBg = 'ffffff' // White background
  const lightBgHover = 'f8f9fa' // Light gray hover background

  // Check if base color has sufficient contrast with light background
  let foregroundColor = baseColor
  if (!meetsWCAGContrast(baseColor, lightBg)) {
    foregroundColor = adjustColorForContrast(baseColor, lightBg)
  }

  return {
    foreground: foregroundColor,
    background: lightBg,
    foregroundHover: foregroundColor,
    backgroundHover: lightBgHover,
  }
}
